import * as Immutable from 'immutable';
import { updateMultipleElements } from '../actions/elementActions';
import { getElementId, getLabels } from '../../../common/elements/utils/elementPropertyUtils';
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { toArray } from '../../../common/utils/immutableHelper';
import { ELEMENT_UPDATE_TYPE } from '../../../common/elements/elementConstants';
import { GenericReduxThunkAction } from '../../../common/actions/actionTypes';
import { getSelectedLabel } from './labelSelectors';
import { getElements } from '../selectors/elementsSelector';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getMainEditorId } from '../utils/elementEditorUtils';
import { selectSelectedElementChip, isTaskItemChipSelection } from '../chipsPanel/elementChipsSelectors';
import { deselectElementChip } from '../chipsPanel/elementChipsActions';
import { removeTaskItemLabelByTaskId } from '../../../common/tiptap/extensions/taskList/taskListItemLabelUtils';
import { deselectAllElements } from '../selection/selectionActions';
import type { TiptapEditorRegistryCallback } from '../../components/tiptapEditor/store/tiptapEditorStoreTypes';

export type ClientAnalytics = {
    usedSearch?: boolean;
};

export const addLabelToElements = (
    elements: Immutable.List<ImMNElement>,
    labelId: string,
    transactionId?: number,
    analytics?: ClientAnalytics,
) => {
    const updates = toArray(
        elements.map((element) => {
            const elementLabels = getLabels(element) || [];
            if (elementLabels.includes(labelId)) return null;

            return {
                id: getElementId(element),
                changes: {
                    labels: [...elementLabels, labelId],
                },
                meta: {
                    addedLabelIds: [labelId],
                },
            };
        }),
    )?.filter(Boolean);

    return {
        ...updateMultipleElements({
            transactionId,
            updates,
            updateType: ELEMENT_UPDATE_TYPE.LABEL,
        }),
        analytics,
    };
};

export const removeLabelFromElements = (elements: Immutable.List<ImMNElement>, labelId: string) => {
    const updates = toArray(
        elements.map((element) => {
            const elementLabels = getLabels(element) || [];
            if (!elementLabels.includes(labelId)) return null;

            return {
                id: getElementId(element),
                changes: {
                    labels: elementLabels.filter((id: string) => id !== labelId),
                },
                meta: {
                    removedLabelIds: [labelId],
                },
            };
        }),
    )?.filter(Boolean);

    return updateMultipleElements({
        updates,
        updateType: ELEMENT_UPDATE_TYPE.TOGGLE_LABEL,
    });
};

/**
 * Removes the selected task-item label chip from its task document.
 */
export const removeSelectedTaskItemLabelChip =
    (getTiptapEditor: TiptapEditorRegistryCallback): GenericReduxThunkAction =>
    (dispatch, getState) => {
        const state = getState() as Immutable.Map<string, unknown>;
        const chipSelection = selectSelectedElementChip(state);
        if (!chipSelection || chipSelection.chip.type !== 'label' || !isTaskItemChipSelection(chipSelection)) {
            return false;
        }

        const elements = getElements(state);
        const element = getElement(elements, chipSelection.elementId);
        if (!element) return false;

        // We need to use the editor registry to lookup the Tiptap editor, because
        //  the Tiptap editor won't be active when the label chip is selected
        const editor = getTiptapEditor(getMainEditorId({ element }));
        if (!editor) return false;

        const didRemoveLabel = removeTaskItemLabelByTaskId({
            editor,
            taskId: chipSelection.containerId,
            labelId: chipSelection.chip.label._id,
        });
        if (didRemoveLabel) dispatch(deselectElementChip());

        return didRemoveLabel;
    };

/**
 * Removes the selected element-owned label chip and clears its canvas selection.
 */
export const removeSelectedElementLabelChip = (): GenericReduxThunkAction => (dispatch, getState) => {
    const state = getState() as Immutable.Map<string, unknown>;
    const chipSelection = selectSelectedElementChip(state);
    if (!chipSelection || chipSelection.chip.type !== 'label' || isTaskItemChipSelection(chipSelection)) return false;

    const labelId = chipSelection.chip.label._id;
    const elements = getElements(state);
    const owner = getElement(elements, chipSelection.elementId);
    if (!owner || !getLabels(owner)?.includes(labelId)) return false;

    dispatch(removeLabelFromElements(Immutable.List([owner]), labelId));
    dispatch(deselectAllElements());
    return true;
};

/**
 * Removes the legacy selected label from its owning element and clears the canvas selection.
 */
export const removeSelectedElementLegacyLabel = (): GenericReduxThunkAction => (dispatch, getState) => {
    const state = getState() as Immutable.Map<string, unknown>;
    const selectedLabel = getSelectedLabel(state);
    if (!selectedLabel) return false;

    const elements = getElements(state);
    const selectedLabelElement = getElement(elements, selectedLabel.elementId);
    if (!selectedLabelElement || !getLabels(selectedLabelElement)?.includes(selectedLabel._id)) return false;

    dispatch(removeLabelFromElements(Immutable.List([selectedLabelElement]), selectedLabel._id));
    dispatch(deselectAllElements());
    return true;
};

/**
 * Delegates selected-label removal to the command for its current owner.
 */
export const removeSelectedLabel =
    (getTiptapEditor: TiptapEditorRegistryCallback): GenericReduxThunkAction =>
    (dispatch, getState) => {
        const state = getState() as Immutable.Map<string, unknown>;
        const chipSelection = selectSelectedElementChip(state);

        if (!chipSelection || chipSelection.chip.type !== 'label') {
            return dispatch(removeSelectedElementLegacyLabel());
        }

        if (isTaskItemChipSelection(chipSelection)) {
            return dispatch(removeSelectedTaskItemLabelChip(getTiptapEditor));
        }

        return dispatch(removeSelectedElementLabelChip());
    };

export const replaceLabelOnElements = (
    elements: Immutable.List<ImMNElement>,
    oldLabelId: string,
    newLabelId: string,
) => {
    const updates = toArray(
        elements.map((element) => {
            const elementLabels = getLabels(element) || [];
            if (!elementLabels.includes(oldLabelId)) return null;

            return {
                id: getElementId(element),
                changes: {
                    labels: elementLabels.map((id: string) => (id === oldLabelId ? newLabelId : id)),
                },
            };
        }),
    )?.filter(Boolean);

    return updateMultipleElements({ updates });
};
